import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

const LoadingScreen = ({ onFinish }: { onFinish: () => void }) => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<"loading" | "exit">("loading");

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setPhase("exit");
          setTimeout(onFinish, 800);
          return 100;
        }
        return prev + Math.random() * 8 + 2;
      });
    }, 80);
    return () => clearInterval(interval);
  }, [onFinish]);

  const clampedProgress = Math.min(progress, 100);

  return (
    <div
      className={cn(
        "fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[hsl(210,30%,6%)] transition-all duration-700",
        phase === "exit" && "opacity-0 scale-105"
      )}
    >
      {/* Neural network background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated neural nodes */}
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${4 + Math.random() * 6}px`,
              height: `${4 + Math.random() * 6}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: `hsl(213, 55%, ${40 + Math.random() * 30}%)`,
              boxShadow: `0 0 ${10 + Math.random() * 20}px hsl(213, 55%, 44% / 0.4)`,
              animation: `neuralPulse ${2 + Math.random() * 3}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}

        {/* Neural connection lines */}
        <svg className="absolute inset-0 w-full h-full opacity-20">
          {Array.from({ length: 12 }).map((_, i) => (
            <line
              key={i}
              x1={`${10 + Math.random() * 80}%`}
              y1={`${10 + Math.random() * 80}%`}
              x2={`${10 + Math.random() * 80}%`}
              y2={`${10 + Math.random() * 80}%`}
              stroke="hsl(213, 55%, 50%)"
              strokeWidth="0.5"
              style={{
                animation: `neuralLine ${3 + Math.random() * 2}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </svg>

        {/* Central glow */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, hsl(213, 55%, 44% / 0.15) 0%, transparent 70%)",
            animation: "neuralPulse 4s ease-in-out infinite",
          }}
        />
      </div>

      {/* Brain/Neural icon */}
      <div className="relative z-10 mb-8">
        <div className="relative">
          <svg
            width="80"
            height="80"
            viewBox="0 0 80 80"
            fill="none"
            className="animate-pulse-slow"
          >
            {/* Brain outline */}
            <path
              d="M40 10C28 10 18 18 16 28C14 22 8 20 8 28C8 34 12 38 16 40C12 42 8 46 8 52C8 60 14 58 16 52C18 62 28 70 40 70C52 70 62 62 64 52C66 58 72 60 72 52C72 46 68 42 64 40C68 38 72 34 72 28C72 20 66 22 64 28C62 18 52 10 40 10Z"
              stroke="hsl(213, 55%, 55%)"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              style={{
                strokeDasharray: 300,
                strokeDashoffset: 300 - (clampedProgress / 100) * 300,
                transition: "stroke-dashoffset 0.3s ease-out",
                filter: "drop-shadow(0 0 8px hsl(213, 55%, 44% / 0.6))",
              }}
            />
            {/* Center line */}
            <path
              d="M40 15V65"
              stroke="hsl(213, 55%, 50%)"
              strokeWidth="1"
              opacity={clampedProgress > 30 ? "0.6" : "0"}
              style={{ transition: "opacity 0.5s" }}
            />
            {/* Neural connections inside */}
            {[
              "M25 30C30 28 35 32 40 30",
              "M40 30C45 32 50 28 55 30",
              "M22 40C28 38 34 42 40 40",
              "M40 40C46 42 52 38 58 40",
              "M25 50C30 52 35 48 40 50",
              "M40 50C45 48 50 52 55 50",
            ].map((d, i) => (
              <path
                key={i}
                d={d}
                stroke="hsl(213, 55%, 60%)"
                strokeWidth="0.8"
                fill="none"
                opacity={clampedProgress > 15 + i * 12 ? "0.5" : "0"}
                style={{ transition: "opacity 0.6s ease-out" }}
              />
            ))}
          </svg>
        </div>
      </div>

      {/* Brand */}
      <div className="relative z-10 text-center mb-10">
        <h1
          className="text-2xl font-display font-semibold tracking-[0.3em] uppercase"
          style={{
            color: "hsl(213, 55%, 65%)",
            textShadow: "0 0 20px hsl(213, 55%, 44% / 0.4)",
          }}
        >
          Equilybra
        </h1>
        <p
          className="text-xs tracking-[0.5em] uppercase mt-2"
          style={{ color: "hsl(210, 20%, 50%)" }}
        >
          Psicologia Corporativa
        </p>
      </div>

      {/* Progress bar */}
      <div className="relative z-10 w-64">
        <div className="h-[2px] w-full bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-200 ease-out"
            style={{
              width: `${clampedProgress}%`,
              background: "linear-gradient(90deg, hsl(213, 55%, 44%), hsl(213, 55%, 65%))",
              boxShadow: "0 0 12px hsl(213, 55%, 44% / 0.6)",
            }}
          />
        </div>
        <p
          className="text-center mt-3 text-xs font-mono tracking-widest"
          style={{ color: "hsl(210, 20%, 45%)" }}
        >
          {Math.round(clampedProgress)}%
        </p>
      </div>

      <style>{`
        @keyframes neuralPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes neuralLine {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
